/**
 * Os cinco modelos que mais trouxeram pagamento no periodo, no escopo
 * escolhido.
 *
 * Ordena por PAGAMENTOS (contagem), e mostra a taxa ao lado: o ranking da
 * pagina responde "quem mais trouxe dinheiro"; o selo "Melhor da categoria"
 * dos cartoes responde "quem converte melhor para o mesmo publico". Sao
 * perguntas diferentes e as duas importam. Clicar abre a previa na aba
 * Desempenho.
 */

import { Badge, Icon, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatRatio } from '../../../lib/format';
import type { Ranking } from '../desempenho';
import type { MessageTemplate } from '../types';
import { CANAL_ICONE, descreverPublico } from '../vocabulario';
import { Variacao } from './Variacao';

export function RankingModelos({
  lista,
  ranking,
  onAbrir,
  className = '',
}: {
  readonly lista: readonly MessageTemplate[];
  readonly ranking: Ranking;
  readonly onAbrir: (t: MessageTemplate) => void;
  readonly className?: string;
}): JSX.Element {
  const top = [...lista]
    .filter((t) => (t.audience === 'checkout_abandonado' || t.audience === 'plano_vencido') && t.performance.paid > 0)
    .sort((a, b) => b.performance.paid - a.performance.paid || b.performance.recoveredCents - a.performance.recoveredCents || a.name.localeCompare(b.name))
    .slice(0, 5);
  const maior = top[0]?.performance.paid ?? 0;

  return (
    <SectionCard id="ranking-modelos" title="Quem mais trouxe pagamento" icon="estrela" description="Top 5 do período por pagamentos com crédito no próprio toque." className={className}>
      {top.length === 0 ? (
        <p className="rounded-xl border border-dashed border-edge bg-elevated px-4 py-6 text-center text-sm text-ink-muted">
          Nenhum pagamento creditado a um modelo neste período. Assim que o primeiro lead voltar depois de um toque, ele aparece aqui.
        </p>
      ) : (
        <ol className="flex flex-col gap-2">
          {top.map((t, i) => {
            const pos = ranking.get(t.id);
            const largura = maior === 0 ? 0 : Math.max(4, Math.round((t.performance.paid * 100) / maior));
            return (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => onAbrir(t)}
                  className="flex w-full flex-col gap-1.5 rounded-xl border border-line bg-elevated px-3 py-2.5 text-left transition-colors hover:border-brand/50"
                >
                  <span className="flex w-full items-center gap-2">
                    <span className="w-5 shrink-0 text-xs font-semibold tabular-nums text-ink-subtle">{i + 1}º</span>
                    <Icon name={CANAL_ICONE[t.channel]} size="xs" className="shrink-0 text-brand" />
                    <span className="min-w-0 flex-1 truncate font-mono text-xs font-semibold text-ink">{t.name}</span>
                    {pos?.melhor === true ? (
                      <Badge tone="good" icon="estrela">
                        Melhor
                      </Badge>
                    ) : null}
                  </span>
                  <span className="h-1 w-full overflow-hidden rounded-full bg-ink/10" aria-hidden="true">
                    <span className="block h-full rounded-full bg-status-good" style={{ width: `${largura}%` }} />
                  </span>
                  <span className="flex w-full flex-wrap items-center justify-between gap-x-2 text-[11px] text-ink-muted">
                    <span>
                      <span className="font-semibold tabular-nums text-ink">{formatCount(t.performance.paid)}</span> pagaram · {formatRatio(t.performance.paid, t.performance.sent)} ·{' '}
                      {formatBRLFromCents(t.performance.recoveredCents)}
                    </span>
                    <Variacao atual={t.performance.paid} anterior={t.previousPerformance === null ? null : t.previousPerformance.paid} compacto />
                  </span>
                  <span className="truncate text-[11px] text-ink-subtle">{descreverPublico(t)}</span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
}
